import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import styles from "./QuestionTypes.module.css";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import { useDispatch } from "react-redux";
import { handleErrorMessage } from "../../../../../redux/actions/groups";
import { validateQuestion } from "../../../../../utils";
import { useTranslation } from "react-i18next";

const MultipleChoiceQuestion = forwardRef(
  ({ initialData = {}, onSave, onMoveUp, onMoveDonw, onDeleteQuestion }, ref) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    // Controlled local state
    const [question, setQuestion] = useState("");
    const [options, setOptions] = useState(["", ""]);
    const [correctAnswer, setCorrectAnswer] = useState(null); // index
    const [focusedIndex, setFocusedIndex] = useState(null);
    const [maxMarks, setMaxMarks] = useState("");
    const [isRequired, setIsRequired] = useState(false);

    const containerRef = useRef(null);

    // Sync incoming initialData into local state (handles legacy keys too)
    useEffect(() => {
      if (!initialData) return;

      setQuestion(initialData.questionText ?? initialData.text ?? initialData.question ?? "");

      // Normalize incoming choices: support `choices` or legacy `choice`
      let incomingChoices = [];
      if (Array.isArray(initialData.choices) && initialData.choices.length) {
        incomingChoices = initialData.choices.map(
          (c) => c.choiceText ?? c.choice_text ?? c.text ?? ""
        );
      } else if (Array.isArray(initialData.choice) && initialData.choice.length) {
        incomingChoices = initialData.choice.map(
          (c) => c.choice_text ?? c.choiceText ?? c.text ?? ""
        );
      }

      if (incomingChoices.length >= 2) {
        setOptions(incomingChoices);
      } else if (incomingChoices.length === 1) {
        setOptions([incomingChoices[0], ""]);
      } else {
        setOptions(["", ""]);
      }

      // Determine correctAnswer index from incoming choice flags
      const sourceChoices = Array.isArray(initialData.choices)
        ? initialData.choices
        : Array.isArray(initialData.choice)
        ? initialData.choice
        : [];

      if (Array.isArray(sourceChoices) && sourceChoices.length) {
        const idx = sourceChoices.findIndex((c) => !!c.isCorrect || !!c.is_correct);
        setCorrectAnswer(idx >= 0 ? idx : null);
      } else {
        setCorrectAnswer(null);
      }

      setMaxMarks(
        initialData.maxMarks ??
          initialData.marks ??
          initialData.max_marks ??
          (typeof initialData.marks === "number" ? String(initialData.marks) : "") ??
          ""
      );

      setIsRequired(initialData.isMandatory ?? initialData.is_mandatory ?? false);
    }, [initialData]);

    // click outside handler for input focus
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (containerRef.current && !containerRef.current.contains(event.target)) {
          setFocusedIndex(null);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);

    const handleAddOption = () => {
      setOptions((prev) => [...prev, ""]);
      // focus new option
      setFocusedIndex(options.length);
    };

    const handleOptionChange = (index, value) => {
      setOptions((prev) => {
        const next = [...prev];
        next[index] = value;
        return next;
      });
    };

    const handleRemoveOption = (index) => {
      setOptions((prev) => {
        const next = [...prev];
        next.splice(index, 1);
        return next.length ? next : ["", ""]; // keep at least two inputs
      });

      setCorrectAnswer((prev) => {
        if (prev === null) return null;
        if (prev === index) return null;
        return prev > index ? prev - 1 : prev;
      });
    };

    // Helper to preserve incoming choice IDs or generate stable ones
    function generateChoiceId(baseId, index) {
      const src = Array.isArray(initialData.choices)
        ? initialData.choices
        : Array.isArray(initialData.choice)
        ? initialData.choice
        : [];
      if (src[index] && src[index].id) return src[index].id;
      return `mcq-${initialData.id ?? Date.now()}-${index}`;
    }

    // Expose save() to parent
    useImperativeHandle(ref, () => ({
      save: handleSave,
    }));

    const handleSave = () => {
      // Use your existing validator (type "mcq")
      const errorMessage = validateQuestion({
        question,
        marks: maxMarks,
        options,
        correctAnswer,
        type: "mcq",
      });

      if (errorMessage) {
        dispatch(handleErrorMessage(errorMessage));
        return null;
      }

      const payload = {
        id: initialData.id ?? `q-${Date.now()}`,
        type: "multipleChoice",
        questionText: question?.trim() ?? "",
        choices: options.map((option, index) => ({
          id: generateChoiceId(initialData.id, index),
          choiceText: option,
          isCorrect: correctAnswer === index,
        })),
        isMandatory: !!isRequired,
        maxMarks: parseInt(maxMarks, 10) || 0,
        icon: initialData.icon ?? null,
      };

      return payload;
    };

    return (
      <div className={styles.questionContainer}>
        <div className={styles.questionHeader}>
          <div className={styles.questionType}>
            <div className={styles.radioIcon}>
              <div className={styles.radioInner}></div>
            </div>
            <span>{t("quiz.multipleTypeQuestion")}</span>
          </div>

          <div className={styles.questionActions}>
            <button className={styles.actionButton} onClick={onMoveUp} type="button">
              <KeyboardArrowUpIcon />
            </button>
            <button className={styles.actionButton} onClick={onMoveDonw} type="button">
              <KeyboardArrowDownIcon />
            </button>
            <button className={styles.actionButton} onClick={onDeleteQuestion} type="button">
              <DeleteOutlineIcon />
            </button>
            <div className={styles.marksContainer}>
              <span>{t("quiz.maxMarks")}</span>
              <input
                min={"0"}
                type="number"
                className={styles.marksInput}
                value={maxMarks}
                onChange={(e) => setMaxMarks(e.target.value)}
              />
            </div>
            <div className={styles.requiredContainer}>
              <span>{t("quiz.required")}</span>
              <label className={styles.switch}>
                <input
                  type="checkbox"
                  checked={isRequired}
                  onChange={(e) => setIsRequired(e.target.checked)}
                />
                <span className={styles.slider}></span>
              </label>
            </div>
          </div>
        </div>

        <div className={styles.questionContent}>
          <input
            type="text"
            className={styles.questionInput}
            placeholder={t("quiz.question")}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
          />

          <div className={styles.optionsContainer} ref={containerRef}>
            {options.map((option, index) => (
              <div key={index} className={styles.optionItem} tabIndex={-1}>
                <div className={styles.circle}></div>

                <div
                  style={{ display: "flex", width: "100%" }}
                  onMouseDown={() => setFocusedIndex(index)}
                >
                  <input
                    type="text"
                    className={`${styles.optionInput} ${
                      focusedIndex === index ? styles.focusedInputField : ""
                    }`}
                    placeholder={t("PollModal.addOption")}
                    value={option}
                    onChange={(e) => handleOptionChange(index, e.target.value)}
                  />

                  {focusedIndex === index ? (
                    <div className={styles.optionActions}>
                      <button
                        className={styles.removeOptionButton}
                        onClick={() => handleRemoveOption(index)}
                        type="button"
                      >
                        <CloseIcon fontSize="small" />
                      </button>

                      <label className={styles.correctCheckbox}>
                        <input
                          type="checkbox"
                          checked={correctAnswer === index}
                          onChange={() =>
                            setCorrectAnswer((prev) => (prev === index ? null : index))
                          }
                        />
                        <span>{t("quiz.correctAnswer")}</span>
                      </label>
                    </div>
                  ) : correctAnswer === index ? (
                    <div className={styles.tickMarkOnly}>
                      <CheckIcon />
                    </div>
                  ) : null}
                </div>
              </div>
            ))}

            <button
              className={styles.addOptionButton}
              onClick={handleAddOption}
              type="button"
            >
              <AddCircleOutlineIcon sx={{ color: "#F33070" }} />
            </button>
          </div>
        </div>
      </div>
    );
  }
);

export default MultipleChoiceQuestion;
